import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const KEY = "pgc_cookie_consent";
const VERSION = 1;
const MAX_AGE = 1000 * 60 * 60 * 24 * 180;
const CATS = [
  ["necessary", "Tecnici e necessari", "Accesso all'account, carrello, sicurezza e memorizzazione delle tue scelte sui cookie. Sempre attivi: senza questi il sito non funziona."],
  ["preferences", "Preferenze e funzionalità", "Ricordano scelte facoltative tra una visita e l'altra per offrirti un'esperienza più comoda."],
  ["analytics", "Statistici", "Ci aiutano a capire in forma aggregata come viene usato il sito per migliorarlo."],
  ["marketing", "Marketing", "Misurano l'efficacia delle campagne e mostrano offerte più pertinenti su altri siti e social."],
];

export const readConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(KEY));
    return c && c.v === VERSION && Date.now() - c.at < MAX_AGE ? c : null;
  } catch {
    return null;
  }
};

export const openCookieSettings = () => window.dispatchEvent(new Event("pgc-cookie-settings"));

function save(prefs) {
  const c = { v: VERSION, at: Date.now(), prefs: { preferences: !!prefs.preferences, analytics: !!prefs.analytics, marketing: !!prefs.marketing, necessary: true } };
  localStorage.setItem(KEY, JSON.stringify(c));
  window.dispatchEvent(new CustomEvent("pgc-cookie-consent", { detail: c }));
}

function Prefs({ prefs, setPrefs }) {
  return (
    <div className="mt-4 max-h-[45vh] space-y-3 overflow-y-auto pr-1">
      {CATS.map(([k, title, text]) => (
        <div key={k} className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-[#0B0C10] p-4">
          <div>
            <p className="font-semibold text-white">{title}</p>
            <p className="mt-1 text-xs text-slate-400">{text}</p>
          </div>
          <Switch data-testid={`cookie-toggle-${k}`} checked={k === "necessary" || !!prefs[k]} disabled={k === "necessary"}
            onCheckedChange={(v) => setPrefs({ ...prefs, [k]: v })} aria-label={title} />
        </div>
      ))}
    </div>
  );
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({});

  useEffect(() => {
    if (!readConsent()) setOpen(true);
    const show = () => { setPrefs(readConsent()?.prefs || {}); setCustom(true); setOpen(true); };
    window.addEventListener("pgc-cookie-settings", show);
    return () => window.removeEventListener("pgc-cookie-settings", show);
  }, []);

  if (!open) return null;
  const done = (p) => { save(p); setOpen(false); setCustom(false); };
  const none = { preferences: false, analytics: false, marketing: false };
  const btn = "rounded-full px-5 py-2.5 text-sm font-semibold transition-colors";

  return (
    <div role="dialog" aria-modal="false" aria-labelledby="cookie-title" data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5">
      <div className="relative mx-auto max-w-3xl rounded-3xl border border-white/10 bg-[#12141C]/95 p-5 shadow-2xl backdrop-blur-xl sm:p-6">
        <button data-testid="cookie-close" onClick={() => done(none)} aria-label="Chiudi e rifiuta i cookie non necessari"
          className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
        <p id="cookie-title" className="flex items-center gap-2 pr-8 font-display text-lg font-semibold text-white"><Cookie className="h-5 w-5 text-amber-400" /> Rispettiamo la tua privacy</p>
        <p className="mt-2 text-sm text-slate-300">
          Usiamo cookie tecnici necessari al funzionamento del sito e, solo con il tuo consenso, cookie di preferenza, statistici e di marketing.
          Puoi accettarli tutti, rifiutarli o scegliere per categoria; chiudendo con la X rifiuti quelli non necessari. Puoi cambiare idea in qualsiasi momento da «Preferenze cookie» in fondo alla pagina.
          {" "}<Link to="/legal/cookie" className="text-amber-400 underline">Cookie Policy</Link> · <Link to="/legal/privacy" className="text-amber-400 underline">Privacy Policy</Link>
        </p>
        {custom && <Prefs prefs={prefs} setPrefs={setPrefs} />}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          {custom ? (
            <button data-testid="cookie-save" onClick={() => done(prefs)} className={`${btn} border border-white/15 text-white hover:bg-white/10`}>Salva le mie scelte</button>
          ) : (
            <button data-testid="cookie-customize" onClick={() => setCustom(true)} className={`${btn} border border-white/15 text-white hover:bg-white/10`}>Personalizza</button>
          )}
          <button data-testid="cookie-reject" onClick={() => done(none)} className={`${btn} bg-amber-500 text-black hover:bg-amber-400`}>Rifiuta</button>
          <button data-testid="cookie-accept" onClick={() => done({ preferences: true, analytics: true, marketing: true })} className={`${btn} bg-amber-500 text-black hover:bg-amber-400`}>Accetta tutti</button>
        </div>
      </div>
    </div>
  );
}
